// Impuesto sobre la Renta al salario — solo empresas "Servicios con Jornadas especiales".
import { getEmpValueAt } from "./empleadoVersioning";
import { ACTIVIDAD_JORNADAS_ESPECIALES } from "./payroll";

export const RENTA_DEFAULT_2026 = {
  tramos: [
    { desde: 0, hasta: 918000, tarifa: 0 },
    { desde: 918000, hasta: 1347000, tarifa: 10 },
    { desde: 1347000, hasta: 2364000, tarifa: 15 },
    { desde: 2364000, hasta: 4727000, tarifa: 20 },
    { desde: 4727000, hasta: null, tarifa: 25 },
  ],
  credito_hijo: 1710,
  credito_conyuge: 2590,
};

const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

export const tablasRenta = (empresa) =>
  (empresa?.renta_tablas && Object.keys(empresa.renta_tablas).length) ? empresa.renta_tablas : { "2026": RENTA_DEFAULT_2026 };

// Tabla del año del período; si no existe, la del año más reciente anterior.
export function tablaRenta(empresa, fecha) {
  const tablas = tablasRenta(empresa);
  const y = Number(String(fecha || new Date().toISOString()).slice(0, 4));
  const years = Object.keys(tablas).map(Number).sort((a, b) => a - b);
  const pick = years.filter(x => x <= y).pop() ?? years[0];
  return tablas[String(pick)] || RENTA_DEFAULT_2026;
}

export const exentoRenta = (tabla) => Number(tabla?.tramos?.[0]?.hasta) || 0;

export function validarTablaRenta(tabla) {
  const t = tabla?.tramos || [];
  if (!t.length) return "Debe haber al menos un tramo";
  for (let i = 0; i < t.length; i++) {
    const d = Number(t[i].desde) || 0;
    const h = t[i].hasta == null || t[i].hasta === "" ? null : Number(t[i].hasta);
    if (h == null && i !== t.length - 1) return `El tramo ${i + 1} no tiene tope; solo el último puede quedar "en adelante"`;
    if (h != null && h <= d) return `En el tramo ${i + 1}, "Hasta" debe ser mayor que "Desde"`;
    if (i > 0 && d !== Number(t[i - 1].hasta)) return `El tramo ${i + 1} debe empezar donde termina el tramo ${i}`;
    const tar = Number(t[i].tarifa);
    if (!(tar >= 0 && tar <= 100)) return `La tarifa del tramo ${i + 1} debe estar entre 0 y 100`;
  }
  return null;
}

export function calcularRentaMensual(bruto, tabla, { conyuge = false, hijos = 0 } = {}) {
  const base = r2(bruto);
  const tramos = [];
  let impuestoBruto = 0;
  for (const t of tabla.tramos || []) {
    const desde = Number(t.desde) || 0;
    const hasta = t.hasta == null || t.hasta === "" ? Infinity : Number(t.hasta);
    const sobre = Math.max(0, Math.min(base, hasta) - desde);
    const tarifa = Number(t.tarifa) || 0;
    if (sobre > 0 && tarifa > 0) {
      const monto = r2(sobre * tarifa / 100);
      tramos.push({ tarifa, sobre: r2(sobre), monto });
      impuestoBruto += monto;
    }
  }
  impuestoBruto = r2(impuestoBruto);
  const nHijos = Math.max(0, Math.floor(Number(hijos) || 0));
  const creditoConyuge = impuestoBruto > 0 && conyuge ? Number(tabla.credito_conyuge) || 0 : 0;
  const creditoHijos = impuestoBruto > 0 ? nHijos * (Number(tabla.credito_hijo) || 0) : 0;
  const impuestoMes = r2(Math.max(0, impuestoBruto - creditoConyuge - creditoHijos));
  return { base, tramos, impuestoBruto, creditoConyuge, creditoHijos, hijos: nHijos, impuestoMes };
}

export function aplicaRenta(empresa, emp) {
  if (empresa?.actividad_grupo !== ACTIVIDAD_JORNADAS_ESPECIALES) return false;
  return String(emp?.frecuencia_pago || "Mensual").toLowerCase() !== "semanal";
}

// Rebajo del período. brutoPrimera = bruto de la I quincena (modo "segunda").
export function rentaPeriodo({ empresa, emp, calc, periodStart, periodEnd, brutoPrimera = 0 }) {
  const tabla = tablaRenta(empresa, periodEnd);
  const conyuge = !!getEmpValueAt(emp, "renta_conyuge", periodEnd);
  const hijos = getEmpValueAt(emp, "renta_hijos", periodEnd) || 0;
  const modo = getEmpValueAt(emp, "renta_modo", periodEnd) || "quincena";
  const freq = String(emp.frecuencia_pago || "Mensual").toLowerCase();
  const bruto = Number(calc.totalBruto) || 0;
  if (freq === "mensual") {
    const d = calcularRentaMensual(bruto, tabla, { conyuge, hijos });
    return { ...d, modo: "mensual", monto: d.impuestoMes };
  }
  const esPrimera = String(periodStart).slice(8, 10) === "01";
  if (modo === "segunda") {
    if (esPrimera || bruto <= 0) return { modo, diferido: true, monto: 0, base: 0, tramos: [], impuestoMes: 0 };
    const d = calcularRentaMensual(bruto + (Number(brutoPrimera) || 0), tabla, { conyuge, hijos });
    return { ...d, modo, brutoPrimera: r2(brutoPrimera), monto: d.impuestoMes };
  }
  const d = calcularRentaMensual(bruto * 2, tabla, { conyuge, hijos });
  return { ...d, modo: "quincena", monto: r2(d.impuestoMes / 2) };
}

export function aplicarRentaACalc(calc, renta) {
  calc.renta = renta?.monto || 0;
  calc.rentaDetalle = renta && renta.impuestoMes > 0 ? renta : null;
  calc.neto = r2((Number(calc.neto) || 0) - calc.renta);
  return calc;
}
